import React from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { SkeletonText } from '../../shared/Skeleton';
import { SituationCard, EmptyState, Pager, usePaged, formatWhen } from '../kit';
import { SURFACE } from '../layout';
import CardHeader from './CardHeader';
import { BlockError } from './states';
import { CARD_PAGE_SIZE } from './cardPageSize';
import CountBadge from './CountBadge';
import { ageTone, channelTone, messageCountTone, TONE_CLASS } from '../metaTone';

// Conversations (spec §5). Data: fn_client_context.threads — [{ id, channel,
// subject, last_at, messages, resolved, client_facing, summary, sentiment,
// urgency }]. summary / sentiment / urgency are null on unenriched threads, which
// is normal: render the summary when present, else the subject.

// Ten conversations per page (CARD_PAGE_SIZE, shared with Work and Requests); the
// Previous / Next pager (the People card's pager) appears once there are more than ten.
export const THREADS_PAGE_SIZE = CARD_PAGE_SIZE;

const threadHeadline = (t) => t?.summary || t?.subject || 'Conversation';

/**
 * A conversation's detail line as coloured parts: the channel (Email, Chat, …), the message count and the
 * date of the last message, each in its own colour (see metaTone).
 * @param {object} t  A thread from fn_client_context.threads.
 * @param {Date} [now]
 * @returns {Array<{text: string, tone: string|null}>}
 */
export const threadMetaParts = (t, now = new Date()) => [
  t?.channel ? { text: String(t.channel), tone: channelTone(t.channel) } : null,
  t?.messages != null ? { text: t.messages === 1 ? '1 message' : `${t.messages} messages`, tone: messageCountTone(t.messages) } : null,
  t?.last_at && formatWhen(t.last_at) ? { text: formatWhen(t.last_at), tone: ageTone(t.last_at, now) } : null,
].filter(Boolean);

/** The detail line: each part in its tone's colour, joined by " · "; nothing when there are no parts. */
const ThreadMeta = ({ parts }) => (parts.length === 0 ? null : (
  <p className="text-xs capitalize tabular-nums text-gray-500 dark:text-gray-400" title={parts.map((p) => p.text).join(' · ')}>
    {parts.map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 ? ' · ' : null}
        <span data-tone={part.tone || undefined} className={part.tone ? TONE_CLASS[part.tone] : undefined}>{part.text}</span>
      </React.Fragment>
    ))}
  </p>
));

/** An open thread with high urgency needs a person; everything else is calm. */
export const threadTint = (t) => (!t?.resolved && t?.urgency === 'high' ? 'warn' : 'neutral');

/**
 * @param {object} props
 * @param {object[]|null} [props.threads]  fn_client_context.threads
 * @param {boolean} [props.loading]
 * @param {*} [props.error]                Truthy when fn_client_context failed.
 * @param {() => void} [props.onRetry]
 */
export default function ThreadsCard({ threads, loading, error, onRetry }) {
  const list = Array.isArray(threads) ? threads : [];
  // Runs every render, before the early returns, to satisfy rules-of-hooks.
  const { pageRows, page, setPage, pageCount, pageSize, total } = usePaged(list, undefined, THREADS_PAGE_SIZE);

  return (
    <PanelErrorBoundary label="Conversations">
      <section className={SURFACE} aria-label="Conversations">
        <CardHeader
          title="Conversations"
          badge={!loading && !error && list.length > 0 ? <CountBadge count={list.length} noun="conversation" testId="threads-count" /> : null}
        />
        {loading ? (
          <div className="mt-3" role="status" aria-label="Loading">
            <SkeletonText lines={2} lineClass="h-10" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : error ? (
          <BlockError error={error} what="this client's conversations" message="Couldn't load this client's conversations." onRetry={onRetry} />
        ) : list.length === 0 ? (
          <EmptyState variant="inset" message="No recent conversations." />
        ) : (
          <div className="mt-3 space-y-2.5">
            {pageRows.map((t, i) => (
              <SituationCard key={t?.id || i} rail={threadTint(t)} data-testid="thread-row">
                {/* The detail line (channel · messages · date) is always on its own line under the headline. */}
                <div className="space-y-1">
                  <p className="min-w-0 text-sm font-normal leading-snug text-gray-900 first-letter:uppercase dark:text-gray-100">{threadHeadline(t)}</p>
                  <ThreadMeta parts={threadMetaParts(t)} />
                </div>
              </SituationCard>
            ))}
            <Pager page={page} pageCount={pageCount} pageSize={pageSize} total={total} onPage={setPage} />
          </div>
        )}
      </section>
    </PanelErrorBoundary>
  );
}
